/**
 * How much room something actually has, measured rather than assumed.
 *
 * Tailwind's breakpoints are about the *window*, and the list toolbar is not
 * about the window: the same 1200px screen gives it 976px with the rail out
 * and 1140px with the rail collapsed, and a related list on a record page gets
 * whatever is left of a two-column grid. A control row that folded on a media
 * query therefore folded at the wrong moment on half the screens in the
 * product.
 *
 * So it watches its own box. The hook returns a *rung*, not a pixel count, so
 * the thing being measured does not re-render on every pixel of a drag — only
 * when it crosses a line that changes what is drawn.
 */
import { useEffect, useRef, useState } from 'react'

/**
 * The rungs, widest first. Each one is what the row does when it stops fitting.
 *
 *   full     everything, with words on the buttons
 *   icons    the buttons lose their labels
 *   folded   Import and Export fold into the list settings menu
 *   phone    the search box gives up its minimum and the count goes
 */
export type Rung = 'full' | 'icons' | 'folded' | 'phone'

const RUNGS: [number, Rung][] = [
  [880, 'full'],
  [680, 'icons'],
  [520, 'folded'],
  [0, 'phone'],
]

export const atMost = (rung: Rung, limit: Rung): boolean =>
  RUNGS.findIndex(([, name]) => name === rung) >= RUNGS.findIndex(([, name]) => name === limit)

export function useRung<T extends HTMLElement = HTMLDivElement>(): {
  ref: React.RefObject<T>
  rung: Rung
} {
  // `as` because this React's `RefObject<T>` is not nullable and a ref that
  // starts empty is the only kind a DOM ref can be.
  const ref = useRef<T>(null) as React.RefObject<T>
  /*
   * `full` until measured, not `phone`. A row that starts folded and springs
   * open is a flicker on every page load; one that starts open and folds is a
   * flicker only on the narrow screens that need it, and those are the screens
   * where the row was about to be wrong anyway.
   */
  const [rung, setRung] = useState<Rung>('full')

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    const measure = () => {
      const width = node.clientWidth
      setRung(RUNGS.find(([min]) => width >= min)?.[1] ?? 'phone')
    }
    measure()

    // Guarded: jsdom has no ResizeObserver, and a component test should not
    // have to stub one to render a list.
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, rung }
}

/**
 * The width below which this is a phone — one definition, used by the frame
 * and by the list engine.
 *
 * A window query rather than an element measurement, unlike everything else in
 * this file, and the difference is deliberate. The rungs above are about how
 * much room a control row has, which the rail's width changes; this is about
 * what kind of device is being held, which it does not. Measuring the element
 * instead would turn a narrow column on a laptop — a related list beside a
 * record — into a phone layout, which is not what anybody meant by it.
 */
export const PHONE_WIDTH = 720

export function usePhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia(`(max-width: ${PHONE_WIDTH}px)`).matches)

  useEffect(() => {
    const query = window.matchMedia(`(max-width: ${PHONE_WIDTH}px)`)
    const update = () => setPhone(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return phone
}

/**
 * The other window width the product has an opinion about.
 *
 * `usePhone()` answers "is this a phone". This answers "is there room for a
 * column beside the work" — 1180px, below which the quote builder's 340px rail
 * becomes a sticky footer and the record page's does not exist. It is a media
 * query rather than an element measurement for the same reason `usePhone` is:
 * the answer is about the window, and measuring an element would turn a narrow
 * column on a wide screen into a tablet layout.
 */
export const RAIL_WIDTH = 1180

export function useNarrow(breakpoint: number = RAIL_WIDTH): boolean {
  const query = `(max-width: ${breakpoint}px)`
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches)

  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setNarrow(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [query])

  return narrow
}

/**
 * Whether the pointer is a finger — `property-maps-v2.md` §7.
 *
 * Not the same question as "is this a phone": an iPad in landscape is 1024px
 * of room *and* a finger, so it wants the desktop's layout at touch sizes,
 * and a 1024px browser window on a laptop wants neither. `pointer: coarse` is
 * the one thing that tells them apart, and it is a media query for the same
 * reason the two above are — it is a fact about the device, not about a box.
 */
export function useCoarse(): boolean {
  const query = '(pointer: coarse)'
  const [coarse, setCoarse] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches)

  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setCoarse(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return coarse
}
